import L, {
    DivIcon,
    FeatureGroup,
    LatLngBoundsExpression,
    CircleMarker as LeafletCircleMarker,
    Map as LeafletMap,
    Marker as LeafletMarker,
    Polyline as LeafletPolyline,
    TileLayer as LeafletTileLayer,
} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React from 'react';
import {
    buildHereTileUrl,
    buildHereTrafficTileUrl,
    fetchHereRoute,
    getDefaultCenter,
    HereRouteSummary,
    LatLngLike,
    LatLngLiteral,
    metersToMiles,
    normaliseCoordinate,
} from './hereMaps';

type MarkerKind =
  | 'origin'
  | 'destination'
  | 'stop'
  | 'tracking'
  | 'trackingDelayed'
  | 'trackingWaiting'
  | 'trackingOnBoard'
  | 'trackingOnBoardDelayed'
  | 'person'
  | 'flag'
  | 'dot'

export type HereMarker = {
  key?: string
  position: LatLngLike
  kind?: MarkerKind
  label?: string | number
  tooltip?: string
  zIndexOffset?: number
  onClick?: () => void
}

export type HereCircle = {
  key?: string
  position: LatLngLike
  radius?: number
  strokeColor?: string
  strokeOpacity?: number
  strokeWeight?: number
  fillColor?: string
  fillOpacity?: number
  onClick?: () => void
}

type HereRouteMapProps = {
  origin?: LatLngLike
  destination?: LatLngLike
  additionalStops?: LatLngLike[]
  trackedLocations?: LatLngLike[]
  trackingStops?: Array<{ where?: LatLngLike } | LatLngLike>
  markers?: HereMarker[]
  circles?: HereCircle[]
  mapWidth?: string
  mapHeight?: string
  marginLeft?: string
  marginRight?: string
  borderRadius?: string
  boxShadow?: string
  mapFocus?: LatLngLike
  focusUser?: boolean
  getLiveLocation?: boolean
  defaultCenter?: LatLngLike
  defaultZoom?: number
  trimRouteFrom?: LatLngLike
  panTarget?: LatLngLike
  panZoom?: number
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  showRoute?: boolean
  showRouteMarkers?: boolean
  hideOriginMarker?: boolean
  showTrackingStops?: boolean
  showCurrentLocation?: boolean
  allowFitBounds?: boolean
  animateRoute?: boolean
  showTrafficOverlay?: boolean
  mapOptions?: {
    zoomControl?: boolean
    scrollWheelZoom?: boolean
    dragging?: boolean
    doubleClickZoom?: boolean
    touchZoom?: boolean
    zoomLevel?: number
  }
  updateRouteInfo?: (info: {
    estimatedJourneyTime: number
    estimatedJourneyDistance: number
    directionsResponse: any
  }) => void
  onBoundsChange?: (bounds: {
    north: number
    south: number
    east: number
    west: number
  }) => void
  /**
   * Draw a dashed arc between two airports (e.g. for a booked flight leg).
   * Rendered as a curved dotted polyline on top of the base map, independent
   * of the driving route. Omit or pass `undefined` to hide.
   */
  flightArc?: {
    from: LatLngLike
    to: LatLngLike
    color?: string
  }
  /**
   * When this number changes (and `flightArc` endpoints are present), the map
   * runs a Google-Earth-style fly-over from the departure airport, along the
   * arc midpoint, to the arrival airport, then settles back into the framed
   * overview. The parent increments this to "play" the animation; setting it
   * back to its previous value won't re-trigger.
   */
  flightArcFlyoverKey?: number
}

const defaultMapOptions = {
  zoomControl: true,
  scrollWheelZoom: true,
  dragging: true,
  doubleClickZoom: true,
  touchZoom: true,
  zoomLevel: 10,
}

const EMPTY_POINTS: LatLngLike[] = []
const EMPTY_TRACKING_POINTS: Array<{ where?: LatLngLike } | LatLngLike> = []
const EMPTY_MARKERS: HereMarker[] = []
const EMPTY_CIRCLES: HereCircle[] = []

const kindStyles: Record<
  MarkerKind,
  { background: string; border: string; color: string; fallbackLabel: string }
> = {
  origin: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    fallbackLabel: 'A',
  },
  destination: {
    background: '#3b51f6',
    border: '#0943a1',
    color: '#ffffff',
    fallbackLabel: 'B',
  },
  stop: {
    background: '#f0c419',
    border: '#d5a900',
    color: '#12212b',
    fallbackLabel: 'S',
  },
  tracking: {
    background: 'var(--jy-navy-900, #00253d)',
    border: '#001b2b',
    color: '#ffffff',
    fallbackLabel: 'C',
  },
  trackingDelayed: {
    background: 'var(--jy-navy-900, #00253d)',
    border: '#c62828',
    color: '#ffffff',
    fallbackLabel: 'C',
  },
  trackingWaiting: {
    background: 'var(--jy-navy-900, #00253d)',
    border: '#ef8a17',
    color: '#ffffff',
    fallbackLabel: 'C',
  },
  trackingOnBoard: {
    background: '#0b7a32',
    border: '#085924',
    color: '#ffffff',
    fallbackLabel: 'C',
  },
  trackingOnBoardDelayed: {
    background: '#0b7a32',
    border: '#c62828',
    color: '#ffffff',
    fallbackLabel: 'C',
  },
  person: {
    background: '#b6541b',
    border: '#8f4113',
    color: '#ffffff',
    fallbackLabel: 'P',
  },
  flag: {
    background: '#5c1d91',
    border: '#46156f',
    color: '#ffffff',
    fallbackLabel: 'F',
  },
  dot: {
    background: '#0d6efd',
    border: '#0a58ca',
    color: '#ffffff',
    fallbackLabel: '•',
  },
}

const createMarkerIcon = (
  kind: MarkerKind = 'origin',
  label?: string | number,
): DivIcon => {
  const style = kindStyles[kind]
  const shouldPulse =
    kind === 'trackingDelayed' ||
    kind === 'trackingWaiting' ||
    kind === 'trackingOnBoardDelayed'
  const display =
    kind === 'tracking' ||
    kind === 'trackingDelayed' ||
    kind === 'trackingWaiting' ||
    kind === 'trackingOnBoard' ||
    kind === 'trackingOnBoardDelayed'
      ? `
        <svg
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5 11L6.6 6.8C6.9 6 7.6 5.5 8.5 5.5H15.5C16.4 5.5 17.1 6 17.4 6.8L19 11M5 11H19M5 11L4 13V16.5H6M19 11L20 13V16.5H18M8 16.5H16M7.5 14.2H7.51M16.5 14.2H16.51"
            stroke="${style.color}"
            stroke-width="1.8"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      `
      : label ?? style.fallbackLabel

  return L.divIcon({
    className: 'here-leaflet-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    html: `
      <div
        style="
          position:relative;
          width:30px;
          height:30px;
          display:flex;
          align-items:center;
          justify-content:center;
          overflow:visible;
        "
      >
        ${
          shouldPulse
            ? `
              <style>
                @keyframes hereMarkerPulse {
                  0% {
                    transform: scale(0.95);
                    opacity: 0.8;
                  }
                  70% {
                    transform: scale(1.55);
                    opacity: 0;
                  }
                  100% {
                    transform: scale(1.7);
                    opacity: 0;
                  }
                }
                @media (prefers-reduced-motion: reduce) {
                  .here-marker-pulse {
                    animation: none !important;
                    opacity: 0.22 !important;
                  }
                }
              </style>
              <span
                class="here-marker-pulse"
                style="
                  position:absolute;
                  inset:-5px;
                  border-radius:999px;
                  border:2px solid ${style.border};
                  opacity:0;
                  animation: hereMarkerPulse 1.8s ease-out infinite;
                  pointer-events:none;
                "
              ></span>
            `
            : ''
        }
        <div
          style="
            position:relative;
            width:30px;
            height:30px;
            border-radius:999px;
            background:${style.background};
            border:2px solid ${style.border};
            color:${style.color};
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:13px;
            font-weight:700;
            box-shadow:0 2px 8px rgba(0,0,0,0.28);
          "
        >${display}</div>
      </div>
    `,
  })
}

const fitToBounds = (
  map: LeafletMap,
  points: LatLngLiteral[],
  fallbackCenter: LatLngLiteral,
  fallbackZoom: number,
  animate = false,
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  },
) => {
  if (!points.length) {
    map.setView([fallbackCenter.lat, fallbackCenter.lng], fallbackZoom, {
      animate,
    })
    return
  }

  if (points.length === 1) {
    const point = points[0]
    map.setView([point.lat, point.lng], fallbackZoom, { animate })
    return
  }

  const bounds: LatLngBoundsExpression = points.map((point) => [
    point.lat,
    point.lng,
  ])

  map.fitBounds(bounds, {
    paddingTopLeft: [viewportPadding?.left ?? 32, viewportPadding?.top ?? 32],
    paddingBottomRight: [
      viewportPadding?.right ?? 32,
      viewportPadding?.bottom ?? 32,
    ],
    animate,
  })
}

const panToWithPadding = (
  map: LeafletMap,
  point: LatLngLiteral,
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  },
  zoom?: number,
) => {
  const left = viewportPadding?.left ?? 0
  const right = viewportPadding?.right ?? 0
  const top = viewportPadding?.top ?? 0
  const bottom = viewportPadding?.bottom ?? 0
  const nextZoom = typeof zoom === 'number' ? zoom : map.getZoom()
  const projected = map.project([point.lat, point.lng], nextZoom)
  const offsetPoint = L.point(
    projected.x + (left - right) / 2,
    projected.y + (top - bottom) / 2,
  )
  const targetCenter = map.unproject(offsetPoint, nextZoom)

  if (typeof zoom === 'number' && map.getZoom() !== zoom) {
    map.setView(targetCenter, zoom, { animate: true })
    return
  }

  map.panTo(targetCenter, {
    animate: true,
    duration: 1,
    easeLinearity: 0.25,
  })
}

const getTrackingCoordinate = (item: { where?: LatLngLike } | LatLngLike) => {
  if (item && typeof item === 'object' && 'where' in item) {
    return normaliseCoordinate(item.where)
  }

  return normaliseCoordinate(item)
}

const isZeroCoordinate = (point: LatLngLiteral | null) =>
  point?.lat === 0 && point?.lng === 0

const getAddressTooltip = (value: LatLngLike) => {
  if (!value || typeof value !== 'object') {
    return undefined
  }

  if (
    'formattedAddress' in value &&
    typeof value.formattedAddress === 'string'
  ) {
    const formattedAddress = value.formattedAddress.trim()
    return formattedAddress || undefined
  }

  const parts = [
    'addressLine1' in value ? value.addressLine1 : undefined,
    'addressLine2' in value ? value.addressLine2 : undefined,
    'addressLine3' in value ? value.addressLine3 : undefined,
    'town' in value ? value.town : undefined,
    'county' in value ? value.county : undefined,
    'postCode' in value ? value.postCode : undefined,
  ]
    .filter(
      (part): part is string => typeof part === 'string' && part.trim() !== '',
    )
    .map((part) => part.trim())

  return parts.length ? parts.join(', ') : undefined
}

type ResolvedMarker = HereMarker & {
  position: LatLngLiteral
  resolvedKey: string
}

type ResolvedCircle = HereCircle & {
  position: LatLngLiteral
  resolvedKey: string
}

type AnimatedLeafletMarker = LeafletMarker & {
  __animationFrame?: number
  __iconSignature?: string
  __clickHandler?: (() => void) | undefined
  __tooltipSignature?: string | undefined
  __tooltipMouseOverHandler?: (() => void) | undefined
  __tooltipMouseOutHandler?: (() => void) | undefined
  __tooltipElementMouseEnterHandler?: ((event: Event) => void) | undefined
  __tooltipElementMouseLeaveHandler?: ((event: Event) => void) | undefined
  __tooltipHideTimeout?: number | undefined
}

type AnimatedLeafletPolyline = LeafletPolyline & { __animationFrame?: number }

const cancelAnimation = (target: { __animationFrame?: number }) => {
  if (target.__animationFrame) {
    window.cancelAnimationFrame(target.__animationFrame)
    target.__animationFrame = undefined
  }
}

const getDistanceBetweenPoints = (from: LatLngLiteral, to: LatLngLiteral) =>
  Math.hypot(to.lat - from.lat, to.lng - from.lng)

const animateMarkerTo = (
  marker: AnimatedLeafletMarker,
  nextPosition: LatLngLiteral,
  duration = 240,
) => {
  const current = marker.getLatLng()
  const from = { lat: current.lat, lng: current.lng }

  if (from.lat === nextPosition.lat && from.lng === nextPosition.lng) {
    marker.setLatLng([nextPosition.lat, nextPosition.lng])
    return
  }

  cancelAnimation(marker)
  const start = performance.now()

  const tick = (now: number) => {
    const progress = Math.min(1, (now - start) / duration)
    const eased = 1 - (1 - progress) * (1 - progress)

    marker.setLatLng([
      from.lat + (nextPosition.lat - from.lat) * eased,
      from.lng + (nextPosition.lng - from.lng) * eased,
    ])

    if (progress < 1) {
      marker.__animationFrame = window.requestAnimationFrame(tick)
    } else {
      marker.__animationFrame = undefined
    }
  }

  marker.__animationFrame = window.requestAnimationFrame(tick)
}

const markerIconSignature = (kind?: MarkerKind, label?: string | number) =>
  `${kind ?? 'origin'}:${label ?? ''}`

const getPolylineLength = (points: LatLngLiteral[]) =>
  points.reduce((total, point, index) => {
    if (index === 0) {
      return total
    }

    return total + getDistanceBetweenPoints(points[index - 1], point)
  }, 0)

const clipPolylineToDistance = (
  points: LatLngLiteral[],
  maxDistance: number,
): LatLngLiteral[] => {
  if (!points.length || maxDistance <= 0) {
    return []
  }

  if (points.length === 1) {
    return [points[0]]
  }

  const clipped: LatLngLiteral[] = [points[0]]
  let traversed = 0

  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]
    const current = points[index]
    const segmentLength = getDistanceBetweenPoints(previous, current)

    if (traversed + segmentLength <= maxDistance) {
      clipped.push(current)
      traversed += segmentLength
      continue
    }

    const remaining = maxDistance - traversed

    if (segmentLength <= 0 || remaining <= 0) {
      break
    }

    const ratio = remaining / segmentLength
    clipped.push({
      lat: previous.lat + (current.lat - previous.lat) * ratio,
      lng: previous.lng + (current.lng - previous.lng) * ratio,
    })
    break
  }

  return clipped
}

const projectPointOnSegment = (
  point: LatLngLiteral,
  from: LatLngLiteral,
  to: LatLngLiteral,
) => {
  const dx = to.lng - from.lng
  const dy = to.lat - from.lat
  const lengthSquared = dx * dx + dy * dy

  if (lengthSquared === 0) {
    return {
      point: from,
      distanceSquared:
        (point.lng - from.lng) * (point.lng - from.lng) +
        (point.lat - from.lat) * (point.lat - from.lat),
    }
  }

  const t = Math.max(
    0,
    Math.min(
      1,
      ((point.lng - from.lng) * dx + (point.lat - from.lat) * dy) /
        lengthSquared,
    ),
  )

  const projected = {
    lat: from.lat + dy * t,
    lng: from.lng + dx * t,
  }

  return {
    point: projected,
    distanceSquared:
      (point.lng - projected.lng) * (point.lng - projected.lng) +
      (point.lat - projected.lat) * (point.lat - projected.lat),
  }
}

const trimPolylinesFromPoint = (
  polylines: LatLngLiteral[][],
  trimPoint: LatLngLiteral | null,
) => {
  if (!trimPoint || !polylines.length) {
    return polylines
  }

  let best: {
    sectionIndex: number
    segmentIndex: number
    projected: LatLngLiteral
    distanceSquared: number
  } | null = null

  polylines.forEach((section, sectionIndex) => {
    if (section.length === 0) {
      return
    }

    if (section.length === 1) {
      const onlyPoint = section[0]
      const distanceSquared =
        (trimPoint.lng - onlyPoint.lng) * (trimPoint.lng - onlyPoint.lng) +
        (trimPoint.lat - onlyPoint.lat) * (trimPoint.lat - onlyPoint.lat)

      if (!best || distanceSquared < best.distanceSquared) {
        best = {
          sectionIndex,
          segmentIndex: 0,
          projected: onlyPoint,
          distanceSquared,
        }
      }
      return
    }

    for (let index = 1; index < section.length; index += 1) {
      const from = section[index - 1]
      const to = section[index]
      const candidate = projectPointOnSegment(trimPoint, from, to)

      if (!best || candidate.distanceSquared < best.distanceSquared) {
        best = {
          sectionIndex,
          segmentIndex: index - 1,
          projected: candidate.point,
          distanceSquared: candidate.distanceSquared,
        }
      }
    }
  })

  if (!best) {
    return polylines
  }

  const trimmed = polylines
    .slice(best.sectionIndex)
    .map((section, relativeSectionIndex) => {
      if (relativeSectionIndex !== 0) {
        return section
      }

      if (section.length <= 1) {
        return section
      }

      const prefix = section.slice(best.segmentIndex + 1)
      const first = prefix[0]
      const startsAtProjected =
        first &&
        first.lat === best.projected.lat &&
        first.lng === best.projected.lng

      if (startsAtProjected) {
        return prefix
      }

      return [best.projected, ...prefix]
    })
    .filter((section) => section.length > 0)

  return trimmed.length ? trimmed : polylines
}

const buildPolylineCollectionSignature = (polylines: LatLngLiteral[][]) =>
  polylines
    .map((points) =>
      points.map((point) => `${point.lat},${point.lng}`).join('|'),
    )
    .join('::')

const makePolyline = (points: LatLngLiteral[]) =>
  L.polyline(
    points.map((point) => [point.lat, point.lng] as [number, number]),
    {
      color: 'var(--jy-navy-900, #00253d)',
      weight: 5,
      opacity: 0.9,
    },
  ) as AnimatedLeafletPolyline

const circleSignature = (circle: ResolvedCircle) =>
  [
    circle.radius ?? 18,
    circle.strokeColor ?? '#2f80ed',
    circle.strokeOpacity ?? 0.75,
    circle.strokeWeight ?? 1.5,
    circle.fillColor ?? '#2f80ed',
    circle.fillOpacity ?? 0.42,
  ].join(':')

const buildViewportSignature = ({
  allowFitBounds,
  routeVisible,
  markerDefinitions,
  routePolylines,
  viewportPadding,
  arcFrom,
  arcTo,
}: {
  allowFitBounds: boolean
  routeVisible: boolean
  markerDefinitions: ResolvedMarker[]
  routePolylines: LatLngLiteral[][]
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  arcFrom?: LatLngLiteral | null
  arcTo?: LatLngLiteral | null
}) => {
  if (!allowFitBounds) {
    return null
  }

  return [
    routeVisible ? 'route' : 'markers',
    markerDefinitions
      .map((marker) => marker.resolvedKey)
      .sort()
      .join('|'),
    routePolylines.length,
    viewportPadding?.top ?? 32,
    viewportPadding?.right ?? 32,
    viewportPadding?.bottom ?? 32,
    viewportPadding?.left ?? 32,
    arcFrom ? `${arcFrom.lat},${arcFrom.lng}` : '',
    arcTo ? `${arcTo.lat},${arcTo.lng}` : '',
  ].join('::')
}

const buildRouteRequestKey = ({
  origin,
  destination,
  via,
}: {
  origin: LatLngLiteral
  destination: LatLngLiteral
  via: LatLngLiteral[]
}) =>
  [
    `${origin.lat},${origin.lng}`,
    `${destination.lat},${destination.lng}`,
    via.map((point) => `${point.lat},${point.lng}`).join('|'),
  ].join('::')

function HereRouteMap({
  origin,
  destination,
  additionalStops = EMPTY_POINTS,
  trackedLocations = EMPTY_POINTS,
  trackingStops = EMPTY_TRACKING_POINTS,
  markers = EMPTY_MARKERS,
  circles = EMPTY_CIRCLES,
  mapWidth = '100%',
  mapHeight = '250px',
  marginLeft,
  marginRight,
  borderRadius,
  boxShadow,
  mapFocus,
  focusUser,
  getLiveLocation,
  defaultCenter,
  defaultZoom,
  trimRouteFrom,
  panTarget,
  panZoom,
  viewportPadding,
  showRoute = true,
  showRouteMarkers = true,
  hideOriginMarker = false,
  showTrackingStops = true,
  showCurrentLocation = true,
  allowFitBounds = true,
  animateRoute = true,
  showTrafficOverlay = false,
  mapOptions,
  updateRouteInfo,
  onBoundsChange,
  flightArc,
  flightArcFlyoverKey,
}: HereRouteMapProps) {
  const apiKey = process.env.REACT_APP_HERE_MAPS_API_KEY
  const mapRef = React.useRef<LeafletMap | null>(null)
  const routeLayerRef = React.useRef<FeatureGroup | null>(null)
  const markerLayerRef = React.useRef<FeatureGroup | null>(null)
  const circleLayerRef = React.useRef<FeatureGroup | null>(null)
  const flightArcLayerRef = React.useRef<FeatureGroup | null>(null)
  const trafficLayerRef = React.useRef<LeafletTileLayer | null>(null)
  const routePolylinesRef = React.useRef<AnimatedLeafletPolyline[]>([])
  const markerInstancesRef = React.useRef<
    globalThis.Map<string, AnimatedLeafletMarker>
  >(new globalThis.Map())
  const circleInstancesRef = React.useRef<
    globalThis.Map<
      string,
      LeafletCircleMarker & {
        __signature?: string
        __clickHandler?: (() => void) | undefined
      }
    >
  >(new globalThis.Map())
  const lastViewportSignatureRef = React.useRef<string | null>(null)
  const lastStaticViewRef = React.useRef<string | null>(null)
  const updateRouteInfoRef = React.useRef(updateRouteInfo)
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [currentLocation, setCurrentLocation] = React.useState<LatLngLiteral>()
  const [mapUnavailable, setMapUnavailable] = React.useState(false)
  const [routeSummary, setRouteSummary] =
    React.useState<HereRouteSummary | null>(null)
  const [routeSummaryKey, setRouteSummaryKey] = React.useState<string | null>(
    null,
  )
  const [pendingViewportRouteKey, setPendingViewportRouteKey] = React.useState<
    string | null
  >(null)
  const [readyRouteKey, setReadyRouteKey] = React.useState<string | null>(null)
  const lastRouteRequestKeyRef = React.useRef<string | null>(null)
  const routeAnimationFrameRef = React.useRef<number | null>(null)
  const currentRoutePolylineSignatureRef = React.useRef<string | null>(null)
  const pendingMarkerRemovalKeysRef = React.useRef<Set<string>>(new Set())

  const resolvedOptions = React.useMemo(
    () => ({
      ...defaultMapOptions,
      ...(mapOptions ?? {}),
    }),
    [mapOptions],
  )

  const fallbackCenter =
    normaliseCoordinate(mapFocus) ??
    (focusUser ? currentLocation : null) ??
    normaliseCoordinate(defaultCenter) ??
    getDefaultCenter()
  const resolvedPanTarget = normaliseCoordinate(panTarget)
  const resolvedTrimRouteFrom = React.useMemo(
    () => normaliseCoordinate(trimRouteFrom),
    [trimRouteFrom],
  )
  const resolvedOrigin = React.useMemo(
    () => normaliseCoordinate(origin),
    [origin],
  )
  const resolvedDestination = React.useMemo(
    () => normaliseCoordinate(destination),
    [destination],
  )
  const resolvedStops = React.useMemo(
    () =>
      additionalStops
        .map((stop) => normaliseCoordinate(stop))
        .filter(
          (stop): stop is LatLngLiteral => !!stop && !isZeroCoordinate(stop),
        ),
    [additionalStops],
  )
  const resolvedTrackedLocations = React.useMemo(
    () =>
      trackedLocations
        .map((stop) => normaliseCoordinate(stop))
        .filter(Boolean) as LatLngLiteral[],
    [trackedLocations],
  )
  const resolvedTrackingStops = React.useMemo(
    () =>
      trackingStops
        .map((stop) => getTrackingCoordinate(stop))
        .filter(Boolean) as LatLngLiteral[],
    [trackingStops],
  )
  const resolvedMarkers = React.useMemo(
    () =>
      markers.reduce<ResolvedMarker[]>((acc, marker, index) => {
        const position = normaliseCoordinate(marker.position)

        if (!position) {
          return acc
        }

        acc.push({
          ...marker,
          position,
          resolvedKey: marker.key ?? `marker-${index}`,
        })

        return acc
      }, []),
    [markers],
  )
  const resolvedCircles = React.useMemo(
    () =>
      circles.reduce<ResolvedCircle[]>((acc, circle, index) => {
        const position = normaliseCoordinate(circle.position)

        if (!position) {
          return acc
        }

        acc.push({
          ...circle,
          position,
          resolvedKey: circle.key ?? `circle-${index}`,
        })

        return acc
      }, []),
    [circles],
  )
  const routeRequestKey = React.useMemo(() => {
    if (!showRoute || !apiKey || !resolvedOrigin || !resolvedDestination) {
      return null
    }

    return buildRouteRequestKey({
      origin: resolvedOrigin,
      destination: resolvedDestination,
      via: [...resolvedStops, ...resolvedTrackedLocations],
    })
  }, [
    apiKey,
    resolvedDestination,
    resolvedOrigin,
    resolvedStops,
    resolvedTrackedLocations,
    showRoute,
  ])
  const routeRequest = React.useMemo(() => {
    if (!routeRequestKey || !resolvedOrigin || !resolvedDestination) {
      return null
    }

    return {
      key: routeRequestKey,
      origin: resolvedOrigin,
      destination: resolvedDestination,
      via: [...resolvedStops, ...resolvedTrackedLocations],
    }
  }, [
    resolvedDestination,
    resolvedOrigin,
    resolvedStops,
    resolvedTrackedLocations,
    routeRequestKey,
  ])

  const fallbackZoom = defaultZoom ?? resolvedOptions.zoomLevel

  React.useEffect(() => {
    updateRouteInfoRef.current = updateRouteInfo
  }, [updateRouteInfo])

  const flushPendingMarkerRemovals = React.useCallback(() => {
    pendingMarkerRemovalKeysRef.current.forEach((key) => {
      const marker = markerInstancesRef.current.get(key)
      if (!marker) {
        return
      }

      cancelAnimation(marker)
      marker.remove()
      markerInstancesRef.current.delete(key)
    })
    pendingMarkerRemovalKeysRef.current.clear()
  }, [])

  React.useEffect(() => {
    if (!getLiveLocation || !navigator.geolocation) {
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      },
      (error) => {
        console.warn('Unable to read current location', error)
      },
      { enableHighAccuracy: true, timeout: 5000, maximumAge: 6000 },
    )
  }, [getLiveLocation])

  React.useEffect(() => {
    if (!apiKey || !containerRef.current || mapRef.current) {
      if (!apiKey) {
        setMapUnavailable(true)
      }
      return
    }

    const map = L.map(containerRef.current, {
      zoomControl: resolvedOptions.zoomControl,
      scrollWheelZoom: resolvedOptions.scrollWheelZoom,
      dragging: resolvedOptions.dragging,
      doubleClickZoom: resolvedOptions.doubleClickZoom,
      touchZoom: resolvedOptions.touchZoom,
    }).setView([fallbackCenter.lat, fallbackCenter.lng], fallbackZoom)

    map.attributionControl?.setPrefix(false)

    L.tileLayer(buildHereTileUrl(apiKey), {
      attribution: '&copy; HERE',
      maxZoom: 20,
      tileSize: 512,
      zoomOffset: -1,
    }).addTo(map)

    routeLayerRef.current = L.featureGroup().addTo(map)
    markerLayerRef.current = L.featureGroup().addTo(map)
    circleLayerRef.current = L.featureGroup().addTo(map)
    flightArcLayerRef.current = L.featureGroup().addTo(map)
    mapRef.current = map
    setMapUnavailable(false)

    const resizeObserver = new ResizeObserver(() => {
      map.invalidateSize()
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
      map.stop()
      routePolylinesRef.current.forEach((polyline) => {
        cancelAnimation(polyline)
        polyline.remove()
      })
      if (routeAnimationFrameRef.current) {
        window.cancelAnimationFrame(routeAnimationFrameRef.current)
        routeAnimationFrameRef.current = null
      }
      pendingMarkerRemovalKeysRef.current.clear()
      markerInstancesRef.current.forEach((marker) => {
        if (marker.__tooltipHideTimeout) {
          window.clearTimeout(marker.__tooltipHideTimeout)
          marker.__tooltipHideTimeout = undefined
        }
        const tooltipElement = marker.getTooltip()?.getElement()
        if (tooltipElement && marker.__tooltipElementMouseEnterHandler) {
          tooltipElement.removeEventListener(
            'mouseenter',
            marker.__tooltipElementMouseEnterHandler,
          )
          marker.__tooltipElementMouseEnterHandler = undefined
        }
        if (tooltipElement && marker.__tooltipElementMouseLeaveHandler) {
          tooltipElement.removeEventListener(
            'mouseleave',
            marker.__tooltipElementMouseLeaveHandler,
          )
          marker.__tooltipElementMouseLeaveHandler = undefined
        }
        if (marker.__tooltipMouseOverHandler) {
          marker.off('mouseover', marker.__tooltipMouseOverHandler)
          marker.__tooltipMouseOverHandler = undefined
        }
        if (marker.__tooltipMouseOutHandler) {
          marker.off('mouseout', marker.__tooltipMouseOutHandler)
          marker.__tooltipMouseOutHandler = undefined
        }
        cancelAnimation(marker)
        marker.remove()
      })
      circleInstancesRef.current.forEach((circle) => {
        circle.remove()
      })
      routePolylinesRef.current = []
      markerInstancesRef.current.clear()
      circleInstancesRef.current.clear()
      lastViewportSignatureRef.current = null
      lastStaticViewRef.current = null
      currentRoutePolylineSignatureRef.current = null
      routeLayerRef.current?.remove()
      markerLayerRef.current?.remove()
      circleLayerRef.current?.remove()
      flightArcLayerRef.current?.remove()
      map.remove()
      routeLayerRef.current = null
      markerLayerRef.current = null
      circleLayerRef.current = null
      flightArcLayerRef.current = null
      if (trafficLayerRef.current) {
        trafficLayerRef.current.remove()
        trafficLayerRef.current = null
      }
      mapRef.current = null
    }
  }, [apiKey])

  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !onBoundsChange) return undefined

    const emit = () => {
      const b = map.getBounds()
      onBoundsChange({
        north: b.getNorth(),
        south: b.getSouth(),
        east: b.getEast(),
        west: b.getWest(),
      })
    }
    emit()
    map.on('moveend', emit)
    map.on('zoomend', emit)
    return () => {
      map.off('moveend', emit)
      map.off('zoomend', emit)
    }
  }, [onBoundsChange])

  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !apiKey) return

    if (showTrafficOverlay && !trafficLayerRef.current) {
      trafficLayerRef.current = L.tileLayer(buildHereTrafficTileUrl(apiKey), {
        attribution: '&copy; HERE Traffic',
        maxZoom: 20,
        tileSize: 512,
        zoomOffset: -1,
        opacity: 1,
        className: 'here-traffic-overlay',
      }).addTo(map)
    } else if (!showTrafficOverlay && trafficLayerRef.current) {
      trafficLayerRef.current.remove()
      trafficLayerRef.current = null
    }
  }, [apiKey, showTrafficOverlay])

  React.useEffect(() => {
    const map = mapRef.current
    if (!map || !apiKey) return

    if (showTrafficOverlay && !trafficLayerRef.current) {
      trafficLayerRef.current = L.tileLayer(buildHereTrafficTileUrl(apiKey), {
        attribution: '&copy; HERE Traffic',
        maxZoom: 20,
        tileSize: 512,
        zoomOffset: -1,
        opacity: 1,
        className: 'here-traffic-overlay',
      }).addTo(map)
    } else if (!showTrafficOverlay && trafficLayerRef.current) {
      trafficLayerRef.current.remove()
      trafficLayerRef.current = null
    }
  }, [apiKey, showTrafficOverlay])

  React.useEffect(() => {
    const map = mapRef.current

    if (!map || !resolvedPanTarget) {
      return
    }

    map.stop()
    map.invalidateSize(false)
    panToWithPadding(map, resolvedPanTarget, viewportPadding, panZoom)
  }, [
    panZoom,
    resolvedPanTarget?.lat,
    resolvedPanTarget?.lng,
    viewportPadding?.bottom,
    viewportPadding?.left,
    viewportPadding?.right,
    viewportPadding?.top,
  ])

  React.useEffect(() => {
    if (!routeRequest) {
      setPendingViewportRouteKey(null)
      setReadyRouteKey(null)
      lastRouteRequestKeyRef.current = null
      flushPendingMarkerRemovals()
      return
    }

    const previousRouteKey = lastRouteRequestKeyRef.current
    const hasRouteChanged =
      previousRouteKey != null && previousRouteKey !== routeRequest.key

    lastRouteRequestKeyRef.current = routeRequest.key
    setRouteSummary(null)
    setRouteSummaryKey(null)

    if (allowFitBounds && !resolvedPanTarget && hasRouteChanged) {
      setPendingViewportRouteKey(routeRequest.key)
      setReadyRouteKey(null)
      return
    }

    setPendingViewportRouteKey(null)
    setReadyRouteKey(routeRequest.key)
    flushPendingMarkerRemovals()
  }, [
    allowFitBounds,
    flushPendingMarkerRemovals,
    resolvedPanTarget,
    routeRequest?.key,
  ])

  React.useEffect(() => {
    if (!apiKey || !routeRequest || readyRouteKey !== routeRequest.key) {
      setRouteSummary(null)
      return
    }

    const abortController = new AbortController()

    fetchHereRoute({
      apiKey,
      origin: routeRequest.origin,
      destination: routeRequest.destination,
      via: routeRequest.via,
      signal: abortController.signal,
    })
      .then((route) => {
        if (abortController.signal.aborted) {
          return
        }

        setRouteSummary(route)
        setRouteSummaryKey(routeRequest.key)
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return
        }

        console.warn('HERE route fetch failed', error)
        setRouteSummary(null)
        setRouteSummaryKey(routeRequest.key)
      })

    return () => {
      abortController.abort()
    }
  }, [apiKey, readyRouteKey, routeRequest?.key])

  React.useEffect(() => {
    const map = mapRef.current

    if (!map || !pendingViewportRouteKey) {
      return
    }

    let isActive = true
    const completeTransition = () => {
      if (!isActive) {
        return
      }

      setPendingViewportRouteKey((current) =>
        current === pendingViewportRouteKey ? null : current,
      )
      setReadyRouteKey((current) =>
        current === pendingViewportRouteKey ? current : pendingViewportRouteKey,
      )
      flushPendingMarkerRemovals()
    }

    const timeoutId = window.setTimeout(completeTransition, 450)
    map.once('moveend', completeTransition)

    return () => {
      isActive = false
      window.clearTimeout(timeoutId)
      map.off('moveend', completeTransition)
    }
  }, [flushPendingMarkerRemovals, pendingViewportRouteKey])

  React.useEffect(() => {
    const map = mapRef.current
    const routeLayer = routeLayerRef.current
    const markerLayer = markerLayerRef.current
    const circleLayer = circleLayerRef.current

    if (!map || !routeLayer || !markerLayer || !circleLayer) {
      return
    }

    const syncMarkers = (
      nextMarkers: ResolvedMarker[],
      { deferRemoval = false }: { deferRemoval?: boolean } = {},
    ) => {
      const clearTooltipHideTimeout = (marker: AnimatedLeafletMarker) => {
        if (marker.__tooltipHideTimeout) {
          window.clearTimeout(marker.__tooltipHideTimeout)
          marker.__tooltipHideTimeout = undefined
        }
      }

      const detachTooltipHoverHandlers = (marker: AnimatedLeafletMarker) => {
        clearTooltipHideTimeout(marker)

        const tooltipElement = marker.getTooltip()?.getElement()
        if (tooltipElement && marker.__tooltipElementMouseEnterHandler) {
          tooltipElement.removeEventListener(
            'mouseenter',
            marker.__tooltipElementMouseEnterHandler,
          )
          marker.__tooltipElementMouseEnterHandler = undefined
        }
        if (tooltipElement && marker.__tooltipElementMouseLeaveHandler) {
          tooltipElement.removeEventListener(
            'mouseleave',
            marker.__tooltipElementMouseLeaveHandler,
          )
          marker.__tooltipElementMouseLeaveHandler = undefined
        }

        if (marker.__tooltipMouseOverHandler) {
          marker.off('mouseover', marker.__tooltipMouseOverHandler)
          marker.__tooltipMouseOverHandler = undefined
        }

        if (marker.__tooltipMouseOutHandler) {
          marker.off('mouseout', marker.__tooltipMouseOutHandler)
          marker.__tooltipMouseOutHandler = undefined
        }
      }

      const updateTooltipPosition = (marker: AnimatedLeafletMarker) => {
        const tooltip = marker.getTooltip()
        const element = tooltip?.getElement()

        if (!tooltip || !element) {
          return
        }

        const mapSize = map.getSize()
        const markerPoint = map.latLngToContainerPoint(marker.getLatLng())
        const maxTooltipWidth = Math.min(Math.max(360, mapSize.x - 24), 560)

        element.style.minWidth = `200px`
        element.style.maxWidth = `${maxTooltipWidth}px`
        element.style.whiteSpace = 'normal'
        element.style.wordBreak = 'break-word'
        element.style.pointerEvents = 'none'

        const rect = element.getBoundingClientRect()
        let direction: 'top' | 'bottom' | 'left' | 'right' = 'top'

        if (markerPoint.x + rect.width / 2 > mapSize.x - 8) {
          direction = 'left'
        } else if (markerPoint.x - rect.width / 2 < 8) {
          direction = 'right'
        } else if (markerPoint.y - rect.height - 20 < 0) {
          direction = 'bottom'
        }

        tooltip.options.direction = direction
        tooltip.options.offset =
          direction === 'top'
            ? L.point(0, -12)
            : direction === 'bottom'
            ? L.point(0, 12)
            : direction === 'left'
            ? L.point(-12, 0)
            : L.point(12, 0)
        tooltip.update()
      }

      const setTooltipVisibility = (
        marker: AnimatedLeafletMarker,
        visible: boolean,
      ) => {
        const element = marker.getTooltip()?.getElement()

        if (!element) {
          return
        }

        if (visible) {
          updateTooltipPosition(marker)
        }

        element.style.opacity = visible ? '1' : '0'
        element.style.visibility = visible ? 'visible' : 'hidden'
        element.style.pointerEvents = visible ? 'auto' : 'none'
        element.style.transition = 'opacity 120ms ease'
      }

      const attachTooltipHoverHandlers = (marker: AnimatedLeafletMarker) => {
        detachTooltipHoverHandlers(marker)

        const handleMouseOver = () => {
          clearTooltipHideTimeout(marker)
          setTooltipVisibility(marker, true)
        }

        const handleMouseOut = () => {
          clearTooltipHideTimeout(marker)
          marker.__tooltipHideTimeout = window.setTimeout(() => {
            setTooltipVisibility(marker, false)
            marker.__tooltipHideTimeout = undefined
          }, 1000)
        }

        marker.on('mouseover', handleMouseOver)
        marker.on('mouseout', handleMouseOut)
        marker.__tooltipMouseOverHandler = handleMouseOver
        marker.__tooltipMouseOutHandler = handleMouseOut

        const tooltipElement = marker.getTooltip()?.getElement()
        if (tooltipElement) {
          const handleTooltipMouseEnter = () => {
            clearTooltipHideTimeout(marker)
            setTooltipVisibility(marker, true)
          }

          const handleTooltipMouseLeave = () => {
            clearTooltipHideTimeout(marker)
            marker.__tooltipHideTimeout = window.setTimeout(() => {
              setTooltipVisibility(marker, false)
              marker.__tooltipHideTimeout = undefined
            }, 1000)
          }

          tooltipElement.addEventListener('mouseenter', handleTooltipMouseEnter)
          tooltipElement.addEventListener('mouseleave', handleTooltipMouseLeave)
          marker.__tooltipElementMouseEnterHandler = handleTooltipMouseEnter
          marker.__tooltipElementMouseLeaveHandler = handleTooltipMouseLeave
        }

        setTooltipVisibility(marker, false)
      }

      const nextKeys = new Set(nextMarkers.map((marker) => marker.resolvedKey))
      const staleKeys: string[] = []

      nextMarkers.forEach((marker) => {
        const existing = markerInstancesRef.current.get(marker.resolvedKey)
        const nextIconSignature = markerIconSignature(marker.kind, marker.label)

        if (existing) {
          if (existing.__iconSignature !== nextIconSignature) {
            existing.setIcon(createMarkerIcon(marker.kind, marker.label))
            existing.__iconSignature = nextIconSignature
          }
          existing.setZIndexOffset(marker.zIndexOffset ?? 0)
          if (existing.__clickHandler !== marker.onClick) {
            if (existing.__clickHandler) {
              existing.off('click', existing.__clickHandler)
            }
            if (marker.onClick) {
              existing.on('click', marker.onClick)
            }
            existing.__clickHandler = marker.onClick
          }
          if (existing.__tooltipSignature !== marker.tooltip) {
            detachTooltipHoverHandlers(existing)
            if (existing.getTooltip()) {
              existing.unbindTooltip()
            }
            if (marker.tooltip) {
              existing.bindTooltip(marker.tooltip, {
                permanent: true,
                direction: 'top',
                offset: [0, -12],
                interactive: true,
              })
              attachTooltipHoverHandlers(existing)
            }
            existing.__tooltipSignature = marker.tooltip
          }
          animateMarkerTo(existing, marker.position)
          return
        }

        const created = L.marker([marker.position.lat, marker.position.lng], {
          icon: createMarkerIcon(marker.kind, marker.label),
          zIndexOffset: marker.zIndexOffset,
        }) as AnimatedLeafletMarker

        if (marker.onClick) {
          created.on('click', marker.onClick)
        }
        if (marker.tooltip) {
          created.bindTooltip(marker.tooltip, {
            permanent: true,
            direction: 'top',
            offset: [0, -12],
            interactive: true,
          })
        }
        created.__iconSignature = nextIconSignature
        created.__clickHandler = marker.onClick
        created.__tooltipSignature = marker.tooltip

        created.addTo(markerLayer)
        if (marker.tooltip) {
          attachTooltipHoverHandlers(created)
        }
        markerInstancesRef.current.set(marker.resolvedKey, created)
      })

      markerInstancesRef.current.forEach((_marker, key) => {
        if (!nextKeys.has(key)) {
          staleKeys.push(key)
        }
      })

      if (deferRemoval) {
        staleKeys.forEach((key) => pendingMarkerRemovalKeysRef.current.add(key))
        return
      }

      const keysToRemove = new Set([
        ...pendingMarkerRemovalKeysRef.current,
        ...staleKeys,
      ])
      pendingMarkerRemovalKeysRef.current.clear()
      keysToRemove.forEach((key) => {
        const marker = markerInstancesRef.current.get(key)
        if (!marker) {
          return
        }

        detachTooltipHoverHandlers(marker)
        cancelAnimation(marker)
        marker.remove()
        markerInstancesRef.current.delete(key)
      })
    }

    const syncRoutePolylines = (nextPolylines: LatLngLiteral[][]) => {
      const currentPolylines = routePolylinesRef.current
      const nextSignature =
        nextPolylines.length > 0
          ? buildPolylineCollectionSignature(nextPolylines)
          : null

      if (routeAnimationFrameRef.current) {
        window.cancelAnimationFrame(routeAnimationFrameRef.current)
        routeAnimationFrameRef.current = null
      }

      while (currentPolylines.length > nextPolylines.length) {
        const polyline = currentPolylines.pop()
        if (polyline) {
          cancelAnimation(polyline)
          polyline.remove()
        }
      }

      nextPolylines.forEach((points, index) => {
        const existing = currentPolylines[index]

        if (existing) {
          return
        }

        const polyline = makePolyline(points)
        polyline.addTo(routeLayer)
        currentPolylines.push(polyline)
      })

      if (!nextSignature) {
        currentRoutePolylineSignatureRef.current = null
        return
      }

      if (!animateRoute) {
        currentRoutePolylineSignatureRef.current = nextSignature
        currentPolylines.forEach((polyline, index) => {
          polyline.setLatLngs(
            nextPolylines[index].map((point) => [point.lat, point.lng]),
          )
        })
        return
      }

      if (currentRoutePolylineSignatureRef.current === nextSignature) {
        currentPolylines.forEach((polyline, index) => {
          polyline.setLatLngs(
            nextPolylines[index].map((point) => [point.lat, point.lng]),
          )
        })
        return
      }

      currentRoutePolylineSignatureRef.current = nextSignature

      const sectionLengths = nextPolylines.map((points) =>
        getPolylineLength(points),
      )
      const totalLength = sectionLengths.reduce(
        (sum, length) => sum + length,
        0,
      )

      if (totalLength <= 0) {
        currentPolylines.forEach((polyline, index) => {
          polyline.setLatLngs(
            nextPolylines[index].map((point) => [point.lat, point.lng]),
          )
        })
        return
      }

      currentPolylines.forEach((polyline) => {
        polyline.setLatLngs([])
      })

      const duration = Math.min(1800, Math.max(700, totalLength * 18000))
      const start = performance.now()

      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / duration)
        const eased = 1 - (1 - progress) * (1 - progress)
        let remainingDistance = totalLength * eased

        currentPolylines.forEach((polyline, index) => {
          const sectionPoints = nextPolylines[index]
          const sectionLength = sectionLengths[index]

          if (remainingDistance <= 0) {
            polyline.setLatLngs([])
            return
          }

          if (remainingDistance >= sectionLength) {
            polyline.setLatLngs(
              sectionPoints.map((point) => [point.lat, point.lng]),
            )
            remainingDistance -= sectionLength
            return
          }

          const visiblePoints = clipPolylineToDistance(
            sectionPoints,
            remainingDistance,
          )
          polyline.setLatLngs(
            visiblePoints.map((point) => [point.lat, point.lng]),
          )
          remainingDistance = 0
        })

        if (progress < 1) {
          routeAnimationFrameRef.current = window.requestAnimationFrame(tick)
        } else {
          routeAnimationFrameRef.current = null
        }
      }

      routeAnimationFrameRef.current = window.requestAnimationFrame(tick)
    }

    const syncCircles = (nextCircles: ResolvedCircle[]) => {
      const nextKeys = new Set(nextCircles.map((circle) => circle.resolvedKey))

      circleInstancesRef.current.forEach((circle, key) => {
        if (!nextKeys.has(key)) {
          circle.remove()
          circleInstancesRef.current.delete(key)
        }
      })

      nextCircles.forEach((circle) => {
        const existing = circleInstancesRef.current.get(circle.resolvedKey)
        const nextSignature = circleSignature(circle)

        if (existing) {
          if (existing.__signature !== nextSignature) {
            existing.setStyle({
              color: circle.strokeColor ?? '#2f80ed',
              opacity: circle.strokeOpacity ?? 0.85,
              weight: circle.strokeWeight ?? 1.5,
              fillColor: circle.fillColor ?? '#2f80ed',
              fillOpacity: circle.fillOpacity ?? 0.42,
            })
            existing.setRadius(circle.radius ?? 18)
            existing.__signature = nextSignature
          }
          existing.setLatLng([circle.position.lat, circle.position.lng])
          if (existing.__clickHandler !== circle.onClick) {
            if (existing.__clickHandler) {
              existing.off('click', existing.__clickHandler)
            }
            if (circle.onClick) {
              existing.on('click', circle.onClick)
            }
            existing.__clickHandler = circle.onClick
          }
          return
        }

        const created = L.circleMarker(
          [circle.position.lat, circle.position.lng],
          {
            radius: circle.radius ?? 18,
            color: circle.strokeColor ?? '#2f80ed',
            opacity: circle.strokeOpacity ?? 0.55,
            weight: circle.strokeWeight ?? 1.5,
            fillColor: circle.fillColor ?? '#2f80ed',
            fillOpacity: circle.fillOpacity ?? 0.22,
          },
        ) as LeafletCircleMarker & {
          __signature?: string
          __clickHandler?: (() => void) | undefined
        }

        if (circle.onClick) {
          created.on('click', circle.onClick)
        }

        created.__signature = nextSignature
        created.__clickHandler = circle.onClick
        created.addTo(circleLayer)
        circleInstancesRef.current.set(circle.resolvedKey, created)
      })
    }

    const boundsPoints: LatLngLiteral[] = []
    const markerDefinitions: ResolvedMarker[] = []
    let nextRoutePolylines: LatLngLiteral[][] = []
    // Flight arc endpoints - when a dashed flight arc is rendered, we want
    // the bounding box to include both airports so the map pans to show
    // the arc as well as the route/markers.
    const arcFromPt = flightArc?.from
      ? normaliseCoordinate(flightArc.from)
      : null
    const arcToPt = flightArc?.to ? normaliseCoordinate(flightArc.to) : null
    const hasCustomMarkerAtOrigin =
      resolvedOrigin != null &&
      resolvedMarkers.some(
        (marker) =>
          marker.position.lat === resolvedOrigin.lat &&
          marker.position.lng === resolvedOrigin.lng,
      )
    const activeRouteSummary =
      routeRequestKey && routeSummaryKey === routeRequestKey
        ? routeSummary
        : null

    const applyViewport = () => {
      const shouldAnimateViewport =
        pendingViewportRouteKey != null &&
        routeRequestKey === pendingViewportRouteKey &&
        !activeRouteSummary

      if (resolvedPanTarget) {
        map.invalidateSize(false)
        lastViewportSignatureRef.current = null
        lastStaticViewRef.current = null
        return
      }

      const viewportSignature = buildViewportSignature({
        allowFitBounds,
        routeVisible: showRoute,
        markerDefinitions,
        routePolylines: nextRoutePolylines,
        viewportPadding,
        arcFrom: arcFromPt,
        arcTo: arcToPt,
      })

      if (viewportSignature) {
        if (lastViewportSignatureRef.current !== viewportSignature) {
          fitToBounds(
            map,
            boundsPoints,
            fallbackCenter,
            fallbackZoom,
            shouldAnimateViewport,
            viewportPadding,
          )
          lastViewportSignatureRef.current = viewportSignature
        }
        lastStaticViewRef.current = null
        return
      }

      lastViewportSignatureRef.current = null

      const staticViewKey = `${fallbackCenter.lat}:${fallbackCenter.lng}:${fallbackZoom}`

      if (lastStaticViewRef.current !== staticViewKey) {
        map.setView([fallbackCenter.lat, fallbackCenter.lng], fallbackZoom, {
          animate: shouldAnimateViewport,
        })
        lastStaticViewRef.current = staticViewKey
      }
    }

    const shouldDeferMarkerRemoval =
      pendingViewportRouteKey != null &&
      routeRequestKey === pendingViewportRouteKey &&
      !activeRouteSummary

    if (showRoute && resolvedOrigin && resolvedDestination) {
      if (activeRouteSummary) {
        nextRoutePolylines = trimPolylinesFromPoint(
          activeRouteSummary.polylines,
          resolvedTrimRouteFrom,
        )
        nextRoutePolylines.forEach((polyline) => {
          polyline.forEach((point) => boundsPoints.push(point))
        })
        syncRoutePolylines(nextRoutePolylines)

        updateRouteInfoRef.current?.({
          estimatedJourneyTime: Math.round(
            activeRouteSummary.durationSeconds / 60,
          ),
          estimatedJourneyDistance: Math.round(
            metersToMiles(activeRouteSummary.lengthMeters),
          ),
          directionsResponse: activeRouteSummary.raw,
        })
      } else {
        syncRoutePolylines([])
      }

      if (showRouteMarkers) {
        if (!hideOriginMarker && !hasCustomMarkerAtOrigin) {
          markerDefinitions.push({
            resolvedKey: `origin-${routeRequestKey ?? 'route'}`,
            position: resolvedOrigin,
            kind: 'origin',
            tooltip: getAddressTooltip(origin),
          })
        }
        markerDefinitions.push({
          resolvedKey: `destination-${routeRequestKey ?? 'route'}`,
          position: resolvedDestination,
          kind: 'destination',
          tooltip: getAddressTooltip(destination),
        })
        boundsPoints.push(resolvedOrigin, resolvedDestination)
        resolvedStops.forEach((stop, index) => {
          markerDefinitions.push({
            resolvedKey: `stop-${index}-${routeRequestKey ?? 'route'}`,
            position: stop,
            kind: 'stop',
            label: index + 1,
            tooltip: getAddressTooltip(additionalStops[index]),
          })
          boundsPoints.push(stop)
        })
      }

      resolvedTrackingStops.forEach((stop, index) => {
        if (showTrackingStops) {
          markerDefinitions.push({
            resolvedKey: `tracking-${index}`,
            position: stop,
            kind: 'tracking',
          })
          boundsPoints.push(stop)
        }
      })

      resolvedMarkers.forEach((marker) => {
        markerDefinitions.push(marker)
        boundsPoints.push(marker.position)
      })

      resolvedCircles.forEach((circle) => {
        boundsPoints.push(circle.position)
      })

      if (currentLocation && showCurrentLocation && getLiveLocation) {
        markerDefinitions.push({
          resolvedKey: 'current-location',
          position: currentLocation,
          kind: 'dot',
        })
        boundsPoints.push(currentLocation)
      }

      syncCircles(resolvedCircles)
      syncMarkers(markerDefinitions, {
        deferRemoval: shouldDeferMarkerRemoval,
      })
      applyViewport()
    } else {
      nextRoutePolylines = []
      syncRoutePolylines([])

      resolvedTrackingStops.forEach((stop, index) => {
        if (showTrackingStops) {
          markerDefinitions.push({
            resolvedKey: `tracking-${index}`,
            position: stop,
            kind: 'tracking',
          })
          boundsPoints.push(stop)
        }
      })

      resolvedMarkers.forEach((marker) => {
        markerDefinitions.push(marker)
        boundsPoints.push(marker.position)
      })

      resolvedCircles.forEach((circle) => {
        boundsPoints.push(circle.position)
      })

      if (currentLocation && showCurrentLocation && getLiveLocation) {
        markerDefinitions.push({
          resolvedKey: 'current-location',
          position: currentLocation,
          kind: 'dot',
        })
        boundsPoints.push(currentLocation)
      }

      if (
        resolvedOrigin &&
        showRouteMarkers &&
        !hideOriginMarker &&
        !hasCustomMarkerAtOrigin
      ) {
        markerDefinitions.push({
          resolvedKey: 'origin',
          position: resolvedOrigin,
          kind: 'origin',
          tooltip: getAddressTooltip(origin),
        })
        boundsPoints.push(resolvedOrigin)
      }

      if (resolvedDestination && showRouteMarkers) {
        markerDefinitions.push({
          resolvedKey: 'destination',
          position: resolvedDestination,
          kind: 'destination',
          tooltip: getAddressTooltip(destination),
        })
        boundsPoints.push(resolvedDestination)
      }

      resolvedStops.forEach((stop, index) => {
        if (showRouteMarkers) {
          markerDefinitions.push({
            resolvedKey: `stop-${index}`,
            position: stop,
            kind: 'stop',
            label: index + 1,
            tooltip: getAddressTooltip(additionalStops[index]),
          })
          boundsPoints.push(stop)
        }
      })

      // Include the flight-arc endpoints so the fit-bounds call frames both
      // airports together with the rest of the markers/route.
      if (arcFromPt) boundsPoints.push(arcFromPt)
      if (arcToPt) boundsPoints.push(arcToPt)

      syncCircles(resolvedCircles)
      syncMarkers(markerDefinitions)
      applyViewport()
    }
  }, [
    allowFitBounds,
    currentLocation,
    fallbackCenter,
    fallbackZoom,
    getLiveLocation,
    resolvedCircles,
    resolvedDestination,
    resolvedMarkers,
    resolvedOrigin,
    resolvedStops,
    resolvedTrimRouteFrom,
    resolvedPanTarget,
    resolvedTrackingStops,
    routeRequestKey,
    routeSummary,
    routeSummaryKey,
    showCurrentLocation,
    hideOriginMarker,
    showRoute,
    showRouteMarkers,
    showTrackingStops,
    pendingViewportRouteKey,
    animateRoute,
    viewportPadding?.bottom,
    viewportPadding?.left,
    viewportPadding?.right,
    viewportPadding?.top,
    flightArc?.from,
    flightArc?.to,
  ])

  // --- Flight arc (dashed curved polyline between two airports) -----------
  // Declared before the `mapUnavailable` early return so hook-call count is
  // stable between renders.
  const resolvedArcFrom = React.useMemo(
    () => (flightArc?.from ? normaliseCoordinate(flightArc.from) : null),
    [flightArc?.from],
  )
  const resolvedArcTo = React.useMemo(
    () => (flightArc?.to ? normaliseCoordinate(flightArc.to) : null),
    [flightArc?.to],
  )

  React.useEffect(() => {
    const layer = flightArcLayerRef.current
    console.log('[FlightArc:map] effect fired', {
      hasLayer: Boolean(layer),
      resolvedArcFrom,
      resolvedArcTo,
    })
    if (!layer) return

    layer.clearLayers()

    if (!resolvedArcFrom || !resolvedArcTo) return

    // Build a quadratic Bézier arc between the two airports. The control
    // point is the midpoint offset perpendicular to the line vector so the
    // curve "bows" consistently for any bearing.
    const from = resolvedArcFrom
    const to = resolvedArcTo
    const dx = to.lng - from.lng
    const dy = to.lat - from.lat
    const midLat = (from.lat + to.lat) / 2
    const midLng = (from.lng + to.lng) / 2
    const bowFactor = 0.22 // 0 = straight line; larger = more curved
    const ctrlLat = midLat + -dx * bowFactor
    const ctrlLng = midLng + dy * bowFactor

    const NUM_POINTS = 64
    const points: [number, number][] = []
    for (let i = 0; i <= NUM_POINTS; i++) {
      const t = i / NUM_POINTS
      const oneMinusT = 1 - t
      const lat =
        oneMinusT * oneMinusT * from.lat +
        2 * oneMinusT * t * ctrlLat +
        t * t * to.lat
      const lng =
        oneMinusT * oneMinusT * from.lng +
        2 * oneMinusT * t * ctrlLng +
        t * t * to.lng
      points.push([lat, lng])
    }

    const arcColor = flightArc?.color ?? '#002EA5'

    L.polyline(points, {
      color: arcColor,
      weight: 5,
      opacity: 0.85,
      dashArray: '1 8',
      lineCap: 'round',
      lineJoin: 'round',
    }).addTo(layer)

    // Small endpoint dots so the arc clearly terminates at each airport.
    ;[from, to].forEach((pt) => {
      L.circleMarker([pt.lat, pt.lng], {
        radius: 4,
        color: arcColor,
        fillColor: '#ffffff',
        fillOpacity: 1,
        weight: 2,
      }).addTo(layer)
    })
  }, [resolvedArcFrom, resolvedArcTo, flightArc?.color])

  // --- Flight-arc fly-over ------------------------------------------------
  // Runs a Google-Earth-style camera move from the departure airport, along
  // the arc midpoint, to the arrival airport, then settles back into the
  // overview. Triggered by the parent incrementing `flightArcFlyoverKey`.
  // Tiles along the path are prefetched into the browser cache before the
  // animation starts so the camera doesn't fly over blank squares.
  const flyoverInFlightRef = React.useRef(false)
  const lastFlyoverKeyRef = React.useRef<number | undefined>(undefined)
  React.useEffect(() => {
    if (flightArcFlyoverKey == null) return
    if (lastFlyoverKeyRef.current === flightArcFlyoverKey) return
    lastFlyoverKeyRef.current = flightArcFlyoverKey
    const map = mapRef.current
    if (!map || !resolvedArcFrom || !resolvedArcTo) return
    if (flyoverInFlightRef.current) return
    if (
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ) {
      return
    }

    const from = resolvedArcFrom
    const to = resolvedArcTo

    // Sample the same bezier the arc is drawn from so the camera tracks the
    // visible curve rather than a straight great-circle path.
    const dx = to.lng - from.lng
    const dy = to.lat - from.lat
    const midLat = (from.lat + to.lat) / 2
    const midLng = (from.lng + to.lng) / 2
    const bowFactor = 0.22
    const ctrlLat = midLat + -dx * bowFactor
    const ctrlLng = midLng + dy * bowFactor
    const SAMPLES = 24
    const path: LatLngLiteral[] = []
    for (let i = 0; i <= SAMPLES; i++) {
      const t = i / SAMPLES
      const oneMinusT = 1 - t
      path.push({
        lat:
          oneMinusT * oneMinusT * from.lat +
          2 * oneMinusT * t * ctrlLat +
          t * t * to.lat,
        lng:
          oneMinusT * oneMinusT * from.lng +
          2 * oneMinusT * t * ctrlLng +
          t * t * to.lng,
      })
    }
    const midpoint = path[Math.floor(SAMPLES / 2)]

    // Auto-tune the mid-arc zoom to the flight distance: short hops shouldn't
    // zoom out as far as long-haul. Use Leaflet's bounds-zoom calc on the
    // bezier extents (with padding) so the whole arc fits in the viewport.
    const arcLats = path.map((p) => p.lat)
    const arcLngs = path.map((p) => p.lng)
    const arcBounds = L.latLngBounds(
      [Math.min(...arcLats), Math.min(...arcLngs)],
      [Math.max(...arcLats), Math.max(...arcLngs)],
    ).pad(0.15)
    const closeZoom = Math.min(map.getMaxZoom?.() ?? 18, 11)
    const fittedZoom = map.getBoundsZoom(arcBounds, false)
    // Bias one level tighter than the bounds zoom so the arc fills the frame
    // rather than floating in a sea of tiles. Clamp so we never zoom further
    // out than 5 (intercontinental) or further in than 8 (short-hop).
    const midZoom = Math.max(5, Math.min(8, fittedZoom + 1))

    flyoverInFlightRef.current = true

    const flyTo = (latlng: LatLngLiteral, zoom: number, duration: number) =>
      new Promise<void>((resolve) => {
        const onEnd = () => {
          map.off('moveend', onEnd)
          resolve()
        }
        map.once('moveend', onEnd)
        map.flyTo([latlng.lat, latlng.lng], zoom, {
          animate: true,
          duration,
          easeLinearity: 0.25,
        })
      })

    void (async () => {
      try {
        await flyTo(from, closeZoom, 1.4)
        await new Promise((r) => setTimeout(r, 350))
        await flyTo(midpoint, midZoom, 1.8)
        await flyTo(to, closeZoom, 1.8)
        // Land on the destination airport - no overview reframe.
      } finally {
        flyoverInFlightRef.current = false
      }
    })()
  }, [flightArcFlyoverKey, resolvedArcFrom, resolvedArcTo, apiKey])

  if (mapUnavailable) {
    return (
      <div
        style={{
          width: mapWidth,
          height: mapHeight,
          marginLeft,
          marginRight,
          borderRadius,
          boxShadow,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f5f7',
          color: '#5b5b5b',
        }}
      >
        Map unavailable
      </div>
    )
  }

  return (
    <div
      style={{
        marginLeft,
        marginRight,
        width: mapWidth,
        height: mapHeight,
        display: 'block',
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          borderRadius,
          boxShadow,
          overflow: 'hidden',
        }}
      />
    </div>
  )
}

export default React.memo(HereRouteMap)
